import { useCallback } from 'react';
import { axiosInstance, useAxios } from '../axios';

export interface TransportDriver {
  id: number | null;
  lock?: number;
  fullName: string;
  documentNumber: string;
  phone: string;
}

export interface GetTransportDriverParams {
  filterText?: string | string[];
  pageNumber?: number;
  resultSize?: number;
}

export type TransportDriverPayload = Omit<TransportDriver, 'id' | 'lock'>;

const TRANSPORT_DRIVER_URL = '/transportdriver';

export const useGetTransportDrivers = () => {
  const [{ data, loading }, axiosFetch] = useAxios<{
    result: { data: TransportDriver[]; count: number };
  }>({
    method: 'GET',
  });

  const getTransportDrivers = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 25,
    }: GetTransportDriverParams) =>
      axiosFetch({
        url: TRANSPORT_DRIVER_URL,
        params: { filterText, pageNumber, resultSize },
      }),
    [axiosFetch],
  );

  return {
    data: data?.result.data,
    count: data?.result.count ?? 0,
    loading,
    getTransportDrivers,
  };
};

export const useCreateTransportDriver = () => {
  const [{ loading }, axiosFetch] = useAxios({ method: 'POST' });

  const createTransportDriver = useCallback(
    (payload: TransportDriverPayload) =>
      axiosFetch({
        url: TRANSPORT_DRIVER_URL,
        skipErrorToast: true,
        data: payload,
      } as any),
    [axiosFetch],
  );

  return { loading, createTransportDriver };
};

export const useUpdateTransportDriver = () => {
  const [{ loading }, axiosFetch] = useAxios({ method: 'PUT' });

  const updateTransportDriver = useCallback(
    (id: number, payload: TransportDriverPayload) =>
      axiosFetch({
        url: `${TRANSPORT_DRIVER_URL}/${id}`,
        skipErrorToast: true,
        data: payload,
      } as any),
    [axiosFetch],
  );

  return { loading, updateTransportDriver };
};

export const useDeleteTransportDriver = () => {
  const deleteTransportDriver = useCallback(
    (id: number) => axiosInstance.delete(`${TRANSPORT_DRIVER_URL}/${id}`),
    [],
  );

  return { deleteTransportDriver };
};
